import { ApolloClient } from '@apollo/client';
import { isNumber } from '@perch-labs/warbler';
import {
  Flex_ValidateProjectIdQueryVariables,
  Flex_ValidateProjectIdQuery,
} from '~/typings/graphql';
import { UnparsedQueryParams } from '../../types';
import { VALIDATE_PROJECT_ID as query } from './handle-project-id.queries';

export { VALIDATE_PROJECT_ID } from './handle-project-id.queries';

export type ProjectIdValidationResult =
  | { status: 'valid'; projectId: number }
  | { status: 'invalid' }
  | { status: 'skipped' };

export const handleProjectId = async ({
  apolloClient,
  projectId,
  utilityId,
}: {
  apolloClient: ApolloClient<{}>;
  projectId: string;
  utilityId: number;
}) => {
  const { data } = await apolloClient.query<
    Flex_ValidateProjectIdQuery,
    Flex_ValidateProjectIdQueryVariables
  >({
    query,
    variables: { vnmSolarProjectId: Number(projectId), utilityId },
  });

  if (data.validation.vnmSolarProject.isValid) {
    return projectId;
  }
  return undefined;
};

/**
 * Early query-param validation: runs when both projectId and utilityId are present
 * (including when the utility step is skipped because utilityId is in the URL).
 */
export const handleProjectIdFromQueryParams = async ({
  apolloClient,
  params,
  utilityId,
}: {
  apolloClient: ApolloClient<{}>;
  params: UnparsedQueryParams;
  utilityId?: number;
}): Promise<ProjectIdValidationResult> => {
  const { projectId } = params;
  const resolvedUtilityId =
    utilityId ?? (isNumber(params.utilityId) ? params.utilityId : undefined);

  if (projectId == null || resolvedUtilityId == null) {
    return { status: 'skipped' };
  }

  const validatedProjectId = await handleProjectId({
    apolloClient,
    projectId: String(projectId),
    utilityId: Number(resolvedUtilityId),
  });

  const parsedProjectId = !isNaN(Number(validatedProjectId))
    ? Number(validatedProjectId)
    : undefined;

  if (!parsedProjectId) {
    return { status: 'invalid' };
  }

  return { status: 'valid', projectId: parsedProjectId };
};
